import { supabase } from "@/integrations/supabase/client";
import {
  getNextScheduledDate,
  type PreventativeFrequency,
} from "@/hooks/usePreventativeMaintenance";

/** After a preventative maintenance task is completed, create the next occurrence and advance PPM date. */
export async function scheduleNextPreventativeMaintenanceOnComplete(
  taskId: string
): Promise<void> {
  const { data: completedTask } = await supabase
    .from("cleaning_tasks")
    .select("task_type, preventative_maintenance_id, property_id")
    .eq("id", taskId)
    .maybeSingle();

  if (
    completedTask?.task_type !== "preventative_maintenance" ||
    !completedTask.preventative_maintenance_id
  ) {
    return;
  }

  const { data: ppm } = await supabase
    .from("property_preventative_maintenance")
    .select("*, preventative_maintenance_templates(*)")
    .eq("id", completedTask.preventative_maintenance_id)
    .maybeSingle();

  if (!ppm?.enabled || !ppm.checklist_id || !ppm.next_scheduled_date) {
    return;
  }

  const title =
    ppm.custom_title ??
    ppm.preventative_maintenance_templates?.title ??
    "Maintenance";
  const nextDate = ppm.next_scheduled_date;

  const { data: newTask } = await supabase
    .from("cleaning_tasks")
    .insert({
      property_id: completedTask.property_id,
      scheduled_date: nextDate,
      checklist_id: ppm.checklist_id,
      cleaner_id:
        ppm.default_assign_type === "cleaner" ? ppm.default_cleaner_id : null,
      assignee_user_id:
        ppm.default_assign_type === "team"
          ? ppm.default_assignee_user_id
          : null,
      assigned_type: ppm.default_assign_type ?? "unassigned",
      notes: `Preventative maintenance: ${title}`,
      status: "scheduled",
      task_type: "preventative_maintenance",
      preventative_maintenance_id: ppm.id,
    })
    .select("id")
    .single();

  if (newTask) {
    const freq = ppm.frequency as PreventativeFrequency;
    const nextNext = getNextScheduledDate(
      freq,
      new Date(nextDate + "T12:00:00")
    );
    await supabase
      .from("property_preventative_maintenance")
      .update({ next_scheduled_date: nextNext })
      .eq("id", ppm.id);
  }
}
